import { uploadFile } from "./upload";
import sleep from "./sleep";
import { apiUpload } from "../services";
import { metrics } from "./metrics";
// import { useGtm } from "@/gtm";

class GifStatusError extends Error {}
class GifUploaderError extends Error {}

interface IProps {
  ticketId: string | null;
  fetchUrl: string | null;
  file: File | undefined;
  isPrivate: boolean;
  keepAudio: boolean;
  tags: string[];
  niches: string[];
  sexuality: string;
  startTime: number;
  endTime: number;
  description: string | null;
}
export const createGif = async (
  { ticketId, fetchUrl, description, file, isPrivate, keepAudio, tags, niches, startTime, sexuality, endTime }: IProps,
  progressCallback: Function
): Promise<string> => {
  // useGtm().send("uploadStarted", { context: "create" });

  const { id } = await submit({
    ticketID: ticketId || (file && (await upload(file, () => {})).id),
    isPrivate,
    keepAudio,
    tags,
    description,
    niches,
    sexuality,
    startTime,
    endTime
  });

  await metrics.sendUploadStarted({ context: "create" });

  await checkUploadStatus(id, progressCallback);

  await metrics.sendUploadFinisher({ sourceURL: file ? "local" : "url" });
  await metrics.sendEncodingStarted({});
  await metrics.sendEncodingFinished();

  return id;
};

export const createGifs = async (files: any, progressCallback: Function) => {
  try {
    const { method, tickets } = await apiUpload.getUploadTicketBatch(files.length);
    const data = await Promise.all(files.map(async (el: any, index: number) => {
      const { id: ticketId, url } = tickets[index];
      const { id } = await upload(el.file, () => {}, { url, method, id: ticketId });
      return id;
    }));

    const { gifs } = await apiUpload.submitGifs({ files: data });
    await metrics.sendUploadStarted({ context: "create" });
    await Promise.all(gifs.map((async (gif, index) => await checkUploadStatus(gif, progressCallback, index))));
    await metrics.sendUploadFinisher({});
    await metrics.sendEncodingStarted({});
    await metrics.sendEncodingFinished();

    return { gifs };
  } catch (e) {
    throw new Error("Error submitting the gif, please try again later.");
  }
};

/**
 * @param {File} file
 * @param {function} progressCallback
 * @returns {Promise<string|undefined>}
 */
export const upload = async (
  file: File,
  progressCallback: Function,
  ticketInfo?: any
) => {
  try {
    return await uploadFile(file, (sent: any) => {
      progressCallback({
        // eslint-disable-next-line
        percent: Math.round((sent * 100) / file.size),
        status: "Uploading..."
      });
    }, ticketInfo);
  } catch (e) {
    throw new GifUploaderError("Error uploading a file.");
  }
};

interface ISubmitProps {
  ticketID: string | null;
  isPrivate: boolean;
  keepAudio: boolean;
  tags: string[];
  niches: string[];
  sexuality: string;
  description: string | null;
  startTime: number;
  endTime: number;
}

const submit = async ({
  ticketID,
  isPrivate,
  keepAudio,
  tags,
  niches,
  sexuality,
  description,
  startTime,
  endTime
}: ISubmitProps) => {
  try {
    return await apiUpload.submitGif({
      ticketID,
      tags,
      niches,
      isPrivate,
      description,
      keepAudio,
      sexuality,
      startTime,
      endTime
    });
  } catch (e) {
    throw new GifUploaderError(
      "Error submitting the gif, please try again later."
    );
  }
};

const checkUploadStatus = async (id: string, progressCallback: Function, index?: number) => {
  let status;

  do {
    // eslint-disable-next-line
    const { task, progress, time } = await apiUpload.checkUploadStatus(id);

    status = task;

    if (status === "error") {
      throw new GifStatusError("Error processing the file.");
    } else if (status === "complete") {
      progressCallback({
        status: "complete",
        gifId: id,
        index
      });
      break;
    } else {
      progressCallback({
        percent: progress,
        duration: time,
        status: "Encoding...",
        gifId: id,
        index
      });
      // eslint-disable-next-line
      await sleep(1000);
    }
  } while (status === "encoding");
};
